import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Company } from '../models/Company';
import { SimplifiedCompany } from '../models/SimplifiedCompany';
import { environment } from '../../../../environments/environment';
import { Place } from '../../account/models/Place';
import { LegalRepresentative } from '../../account/models/LegalRepresentative';
import { PlaceFilterForm } from '../../operator/models/PlaceFilterForm';
import { Page } from 'src/app/shared/model/page';
import { NetworkContractFilterForm } from '../../operator/models/NetworkContractFilterForm';
import { RequestFilterForm } from '../../operator/models/RequestFilterForm';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {

  constructor(private http: HttpClient) {
  }

  getUserOwnedCompanyList(): Observable<Company[]> {
    return this.http.get<Company[]>(environment.loggedInUrl + '/lista-companii');
  }

  getAllUserCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(environment.loggedInUrl + '/lista-companii-cu-imputernicire');
  }

  getSimplifiedUserOwnedCompanyList(): Observable<SimplifiedCompany[]> {
    return this.http.get<SimplifiedCompany[]>(environment.loggedInUrl + '/lista-companii-simplificata');
  }

  getCompanyDetails(clientId: number): Observable<Company> {
    return this.http.get<Company>(environment.loggedInUrl + '/detalii-companie/client/' + clientId);
  }

  addCompanyDetails(formData: FormData): Observable<Company> {
    return this.http.put<Company>(environment.loggedInUrl + '/adaugare-companie', formData);
  }

  deleteCompany(companyId: number): Observable<Company> {
    return this.http.delete<Company>(environment.loggedInUrl + '/stergere-companie/' + companyId);
  }

  associateCompanyElectricityConsumptionPlace(code: string, companyCode: string): Observable<any> {
    const params = new HttpParams().set('companyCode', companyCode).set('code', code);
    return this.http.post(environment.loggedInUrl + '/companie/asociaza-loc-consum-energie-electrica', {}, { params });
  }

  associateCompanyGasConsumptionPlace(code: string, companyCode: string): Observable<any> {
    const params = new HttpParams().set('companyCode', companyCode).set('code', code);
    return this.http.post(environment.loggedInUrl + '/companie/asociaza-loc-consum-gaze-naturale', {}, { params });
  }

  disassociateCompanyElectricityConsumptionPlace(code: string, companyCode: string): Observable<any> {
    const params = new HttpParams().set('companyCode', companyCode).set('code', code);
    return this.http.post(environment.loggedInUrl + '/companie/dezasociaza-loc-consum-energie-electrica', {}, { params });
  }

  disassociateCompanyGasConsumptionPlace(code: string, companyCode: string): Observable<any> {
    const params = new HttpParams().set('companyCode', companyCode).set('code', code);
    return this.http.post(environment.loggedInUrl + '/companie/dezasociaza-loc-consum-gaze-naturale', {}, { params });
  }

  getCompanyElectricityConsumptionPlaces(companyCode: string, placeFilterForm: PlaceFilterForm): Observable<Page<Place>> {
    return this.http.post<Page<Place>>(environment.loggedInUrl + '/companie/energie-electrica/locuri-de-consum/' + companyCode, placeFilterForm);
  }

  getCompanyElectricityContracts(contractFilterForm: NetworkContractFilterForm): Observable<Page<any>> {
    return this.http.post<Page<any>>(environment.baseUrl + '/contract-furnizare/client-final/electricitate', contractFilterForm);
  }

  getCompanyElectricityRequests(electricityRequestFilterForm: RequestFilterForm, companyCode: string): Observable<Page<any>> {
    electricityRequestFilterForm.companyCode = companyCode;
    return this.http.post<Page<any>>(environment.baseUrl + `/cerere/client-final/electricitate`, electricityRequestFilterForm);
  }

  getCompanyGasConsumptionPlaces(companyCode: string, placeFilterForm: PlaceFilterForm): Observable<Page<Place>> {
    return this.http.post<Page<Place>>(environment.loggedInUrl + '/companie/gaze-naturale/locuri-de-consum/' + companyCode, placeFilterForm);
  }

  getCompanyGasContracts(contractFilterForm: NetworkContractFilterForm, companyCode: string, placeId?: number): Observable<Page<any>> {
    contractFilterForm.code = companyCode;
    if (placeId) {
      contractFilterForm.placeId = placeId;
    }
    return this.http.post<Page<any>>(environment.baseUrl + '/contract-furnizare/client-final/gaze', contractFilterForm);
  }

  getCompanyGasRequests(gasRequestFilterForm: RequestFilterForm, companyCode: string): Observable<Page<any>> {
    gasRequestFilterForm.companyCode = companyCode;
    return this.http.post<Page<any>>(environment.baseUrl + `/cerere/client-final/gaze`, gasRequestFilterForm);
  }

  getUserOwnedCompanyLegalRepresentatives(companyCode: string): Observable<LegalRepresentative[]> {
    return this.http.get<LegalRepresentative[]>(environment.baseUrl + '/companie/lista-imputerniciti/' + companyCode);
  }

  updateLegalRepresentativeRoles(companyCode: string, legalRepresentative: LegalRepresentative): Observable<LegalRepresentative> {
    const url = environment.baseUrl + '/companie/' + companyCode + '/modificare-drepturi-imputernicit';
    return this.http.post<LegalRepresentative>(url, legalRepresentative);
  }

  addLegalRepresentative(companyCode: string, legalRepresentative: LegalRepresentative): Observable<LegalRepresentative> {
    const url = environment.baseUrl + '/companie/' + companyCode + '/imputernicire-utilizator';
    return this.http.post<LegalRepresentative>(url, legalRepresentative);
  }

  removeLegalRepresentative(companyCode: string, legalRepresentative: LegalRepresentative): Observable<LegalRepresentative> {
    const url = environment.baseUrl + '/companie/' + companyCode + '/stergere-imputernicit';
    return this.http.post<LegalRepresentative>(url, legalRepresentative);
  }

  cancelRequest(id: number): Observable<void> {
    return this.http.put<void>(environment.baseUrl + `/cerere/${id}/anulare`, {});
  }

  cancelContract(id: number): Observable<void> {
    return this.http.put<void>(environment.baseUrl + `/contract-furnizare/${id}/anulare`, {});
  }
}
